import { ArrowDown, ArrowUp, ChevronRight, LayoutGrid, Sparkles } from "lucide-react";
import type { ConfidenceLevel, SimulationStatus } from "@opus-dt/shared-types";
import { Empty } from "@/components/ui/notify/Empty";
import { Pill } from "@/components/ui/pill";
import { formatRelativeTime, getSimulationStatusLabel } from "@/lib/display-labels";

// Linha unificada do hub (redesign): cenário salvo OU busca automática foldada como
// 1 linha-resumo tagueada. Mapeamento vive no orquestrador (index.tsx).
export interface HubRow {
  id: string;
  kind: "scenario" | "auto-batch";
  name: string;
  base: string;
  period: string | null;
  oee: number | null;
  delta: number | null;
  updatedAt: string;
  runStatus: SimulationStatus | null;
  confidenceLevel: ConfidenceLevel | null;
  /** true = busca automática (read-only, tag). */
  legacy: boolean;
  /** cenário com rodada concluída na mesma linha da seleção corrente. */
  comparable: boolean;
}

interface Props {
  rows: HubRow[];
  selected: string[];
  onToggleSelect: (id: string) => void;
  onOpen: (row: HubRow) => void;
}

export function ScenariosTable({ rows, selected, onToggleSelect, onOpen }: Props) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-sm font-semibold">Cenários salvos</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
          {rows.length}
        </span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          Compartilhados na organização · retratos fiéis do momento
        </span>
      </div>

      {rows.length === 0 ? (
        <Empty
          variant="inline"
          icon={<LayoutGrid className="h-5 w-5" />}
          title="Nenhum cenário salvo"
          description="Comece por “Escolher linha” ou “Montar do acervo” para explorar mudanças sem tocar a produção."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr className="border-b">
                <th className="w-8 py-2 pl-4" />
                <th className="px-2 py-2 font-medium">Cenário</th>
                <th className="px-2 py-2 font-medium">Base</th>
                <th className="px-2 py-2 font-medium">Período</th>
                <th className="px-2 py-2 font-medium">OEE sim.</th>
                <th className="px-2 py-2 font-medium">Δ vs baseline</th>
                <th className="px-2 py-2 font-medium">Atualizado</th>
                <th className="w-8 pr-4" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row
                  key={r.id}
                  row={r}
                  checked={selected.includes(r.id)}
                  onToggle={() => onToggleSelect(r.id)}
                  onOpen={() => onOpen(r)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({
  row,
  checked,
  onToggle,
  onOpen,
}: {
  row: HubRow;
  checked: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="py-2.5 pl-4">
        <input
          type="checkbox"
          aria-label={`Selecionar ${row.name} para comparar`}
          title={
            row.comparable
              ? undefined
              : "Comparação exige cenários da mesma linha com rodada concluída."
          }
          checked={checked}
          disabled={!row.comparable}
          onChange={onToggle}
        />
      </td>
      <td className="px-2 py-2.5">
        <button
          type="button"
          className="flex items-center gap-1.5 text-left font-medium hover:underline"
          onClick={onOpen}
        >
          {row.name}
          {row.legacy ? (
            <Pill tone="neutral" icon={<Sparkles className="h-3 w-3" />}>
              Busca automática
            </Pill>
          ) : null}
        </button>
      </td>
      <td className="px-2 py-2.5 text-muted-foreground">{row.base}</td>
      <td className="px-2 py-2.5 font-mono text-[12px] text-muted-foreground">
        {row.period ?? "—"}
      </td>
      <td className="px-2 py-2.5">
        <OeeCell row={row} />
      </td>
      <td className="px-2 py-2.5">
        <DeltaCell delta={row.delta} />
      </td>
      <td className="px-2 py-2.5 text-[12px] text-muted-foreground">
        {formatRelativeTime(row.updatedAt)}
      </td>
      <td className="pr-4">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </td>
    </tr>
  );
}

function OeeCell({ row }: { row: HubRow }) {
  if (row.runStatus && row.runStatus !== "COMPLETED") {
    return <span className="text-muted-foreground">{getSimulationStatusLabel(row.runStatus)}</span>;
  }
  if (row.oee == null) return <span className="text-muted-foreground">—</span>;
  return <span className="font-mono font-medium tabular-nums">{row.oee.toFixed(1)}%</span>;
}

function DeltaCell({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-muted-foreground">—</span>;
  const up = delta >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-mono text-[12px] tabular-nums ${up ? "text-emerald-600" : "text-destructive"}`}
    >
      {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(delta).toFixed(1)} pp
    </span>
  );
}
